import { useEffect, useRef, useState } from "react";

/**
 * Wraps children (typically a Recharts <ResponsiveContainer>) and only renders them once the
 * host element has an actual non-zero width AND we're past the first paint frame. This
 * eliminates the noisy `width(-1)` Recharts warning that fires when ResponsiveContainer
 * measures its container before layout has settled (e.g. inside display:none tab panels).
 */
export function DeferredChart({ height = 260, minWidth = 0, className = "", children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    let raf1 = 0, raf2 = 0, t = 0;
    const check = () => {
      if (!ref.current) return;
      if (ref.current.getBoundingClientRect().width > 0) { setVisible(true); return; }
      // Not yet — retry next frame.
      raf2 = requestAnimationFrame(check);
    };
    // Wait 2 frames to let CSS layout settle after mount.
    raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(check); });
    // Also poll via IntersectionObserver for hidden tabs that become visible later.
    const io = "IntersectionObserver" in window ? new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.boundingClientRect.width > 0) setVisible(true);
      });
    }, { threshold: 0.01 }) : null;
    if (io && ref.current) io.observe(ref.current);
    // Long-tail fallback
    t = setTimeout(check, 500);
    return () => {
      cancelAnimationFrame(raf1); cancelAnimationFrame(raf2);
      clearTimeout(t);
      if (io) io.disconnect();
    };
  }, [visible]);
  return (
    <div ref={ref} className={className} style={{ width: "100%", height, minWidth }}>
      {visible ? children : null}
    </div>
  );
}
